import React, { memo, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { classNames } from '../../utils/classNames';
import { getAttachmentName } from '../../utils/attachmentUtils';
import { AttachmentCarouselSlide } from './AttachmentCarouselSlide';
import { CarouselHeader } from './CarouselHeader';
import { CarouselNav } from './CarouselNav';
import type { AttachmentCarouselProps } from './AttachmentCarousel.types';
import './AttachmentCarousel.css';

function AttachmentCarouselComponent({
  attachments,
  initialIndex,
  onClose,
  className
}: AttachmentCarouselProps): React.ReactElement | null {
  const [currentIndex, setCurrentIndex] = useState(
    Math.max(0, Math.min(initialIndex, attachments.length - 1))
  );
  const item = attachments[currentIndex];
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < attachments.length - 1;

  const goPrev = useCallback(() => {
    setCurrentIndex(i => Math.max(0, i - 1));
  }, []);

  const goNext = useCallback(() => {
    setCurrentIndex(i => Math.min(attachments.length - 1, i + 1));
  }, [attachments.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && hasPrev) goPrev();
      else if (e.key === 'ArrowRight' && hasNext) goNext();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, hasPrev, hasNext, goPrev, goNext]);

  if (attachments.length === 0) return null;

  const content = (
    <div
      className={classNames('attachment-carousel', className)}
      role="dialog"
      aria-label="Attachment viewer"
      aria-modal="true"
    >
      <div className="attachment-carousel__backdrop" onClick={onClose} />
      <div className="attachment-carousel__container">
        <CarouselHeader
          currentIndex={currentIndex}
          totalCount={attachments.length}
          onClose={onClose}
        />
        <div className="attachment-carousel__main">
          <CarouselNav direction="prev" onClick={goPrev} visible={hasPrev} />
          <div className="attachment-carousel__slide">
            {item && <AttachmentCarouselSlide item={item} />}
          </div>
          <CarouselNav direction="next" onClick={goNext} visible={hasNext} />
        </div>
        {item && (
          <footer className="attachment-carousel__footer">
            {getAttachmentName(item)}
          </footer>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

export const AttachmentCarousel = memo(AttachmentCarouselComponent);
AttachmentCarousel.displayName = 'AttachmentCarousel';
